import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { serviceTypeService } from '../../services/serviceTypeService';
import { BarberCreationRequest, ServiceType, BarberServiceRequest } from '../../types/barber.types';
import { useToast } from '../../context/ToastContext';
import ImageUpload from '../../components/common/ImageUpload';
import AdminHeader from '../../components/admin/AdminHeader';

const CreateBarberPage: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [loading, setLoading] = useState(false);
    const [availableServices, setAvailableServices] = useState<ServiceType[]>([]);

    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        nickname: '',
        phoneNumber: '',
        instagramUrl: '',
        facebookUrl: '',
        profilePictureUrl: '',
        role: 'BARBER' as 'BARBER' | 'SUPERUSER',
        specialization: '',
        yearsOfExperience: 0,
        isIndependent: false
    });

    const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
    const [customPrices, setCustomPrices] = useState<Record<number, number>>({});

    useEffect(() => {
        loadServices();
    }, []);

    const loadServices = async () => {
        try {
            const services = await serviceTypeService.getAllServices();
            setAvailableServices(services);
        } catch (error) {
            console.error('Error loading services:', error);
            showToast('Error al cargar servicios', 'error');
        }
    };

    const toggleService = (serviceId: number) => {
        setSelectedServiceIds(prev =>
            prev.includes(serviceId)
                ? prev.filter(id => id !== serviceId)
                : [...prev, serviceId]
        );
    };

    const handleCustomPriceChange = (serviceId: number, price: string) => {
        const numericPrice = parseFloat(price);
        setCustomPrices(prev => ({
            ...prev,
            [serviceId]: isNaN(numericPrice) ? 0 : numericPrice
        }));
    };

    const handleImageUploaded = (url: string) => {
        setFormData(prev => ({ ...prev, profilePictureUrl: url }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.email || !formData.firstName || !formData.lastName || !formData.nickname || !formData.specialization) {
            showToast('Por favor, completa todos los campos obligatorios', 'error');
            return;
        }

        if (selectedServiceIds.length === 0) {
            showToast('Por favor, selecciona al menos un servicio', 'error');
            return;
        }

        try {
            setLoading(true);

            const servicesRequest: BarberServiceRequest[] = selectedServiceIds.map(id => ({
                serviceTypeId: id,
                customPrice: customPrices[id] || undefined
            }));

            const request: BarberCreationRequest = {
                ...formData,
                phoneNumber: formData.phoneNumber || undefined,
                instagramUrl: formData.instagramUrl || undefined,
                facebookUrl: formData.facebookUrl || undefined,
                profilePictureUrl: formData.profilePictureUrl || undefined,
                pricePerService: 0, // Deprecated
                schedules: [],
                services: servicesRequest,
                isIndependent: formData.isIndependent
            };

            await adminService.createBarber(request);
            showToast(
                `Barbero creado exitosamente. Las credenciales han sido enviadas a ${formData.email}`,
                'success'
            );
            // Redirect to barber management after short delay
            setTimeout(() => navigate('/admin/barbers'), 1500);
        } catch (error: any) {
            console.error('Error creating barber:', error);
            showToast(error.response?.data?.message || 'Error al crear barbero', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <AdminHeader
                title="🧍 Crear Nuevo Barbero"
                backPath="/admin/barbers"
            />
            <div className="container mx-auto p-md md:p-xl" style={{ maxWidth: '1000px' }}>
                <p className="mb-lg" style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)', color: '#a3a3a3' }}>
                    Completa los datos del nuevo barbero. Se generará una contraseña automática.
                </p>

                {/* Form Card */}
                <form onSubmit={handleSubmit}>
                    <div className="card p-md md:p-xl" style={{
                        background: '#111111',
                        borderRadius: '1rem',
                        border: '1px solid #2a2a2a',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
                    }}>
                        {/* Personal Information */}
                        <div className="mb-xl pb-xl" style={{ borderBottom: '1px solid #2a2a2a' }}>
                            <h2 className="font-bold mb-md" style={{ fontSize: 'clamp(1.125rem, 3vw, 1.25rem)', color: '#f59e0b' }}>
                                👤 Información Personal
                            </h2>

                            <div className="space-y-md">
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                                    <div>
                                        <label className="form-label" style={{ color: '#a3a3a3' }}>Nombre *</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                            value={formData.firstName}
                                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                            disabled={loading}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label" style={{ color: '#a3a3a3' }}>Apellido *</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                            value={formData.lastName}
                                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                            disabled={loading}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label" style={{ color: '#a3a3a3' }}>Apodo *</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                            placeholder="Ej: El Grande"
                                            value={formData.nickname}
                                            onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                                            disabled={loading}
                                            required
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                                    <div>
                                        <label className="form-label" style={{ color: '#a3a3a3' }}>Email *</label>
                                        <input
                                            type="email"
                                            className="form-input"
                                            style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            disabled={loading}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label" style={{ color: '#a3a3a3' }}>Teléfono</label>
                                        <input
                                            type="tel"
                                            className="form-input"
                                            style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                            placeholder="3543455433"
                                            value={formData.phoneNumber}
                                            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Profile Picture */}
                        <div className="mb-xl pb-xl" style={{ borderBottom: '1px solid #2a2a2a' }}>
                            <h2 className="font-bold mb-md" style={{ fontSize: '1.25rem', color: '#f59e0b' }}>
                                📷 Foto de Perfil
                            </h2>
                            <ImageUpload
                                currentImageUrl={formData.profilePictureUrl}
                                onImageUploaded={handleImageUploaded}
                            />
                        </div>

                        {/* Professional Info */}
                        <div className="mb-xl pb-xl" style={{ borderBottom: '1px solid #2a2a2a' }}>
                            <h2 className="font-bold mb-md" style={{ fontSize: '1.25rem', color: '#f59e0b' }}>
                                ✂️ Información Profesional
                            </h2>

                            <div className="space-y-md">
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                                    <div>
                                        <label className="form-label" style={{ color: '#a3a3a3' }}>Rol *</label>
                                        <select
                                            className="form-input"
                                            style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                            value={formData.role}
                                            onChange={(e) => setFormData({ ...formData, role: e.target.value as 'BARBER' | 'SUPERUSER' })}
                                            disabled={loading}
                                        >
                                            <option value="BARBER">Barbero</option>
                                            <option value="SUPERUSER">Administrador</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label" style={{ color: '#a3a3a3' }}>Años de Experiencia *</label>
                                        <input
                                            type="number"
                                            className="form-input"
                                            style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                            min="0"
                                            value={formData.yearsOfExperience}
                                            onChange={(e) => setFormData({ ...formData, yearsOfExperience: parseInt(e.target.value) || 0 })}
                                            disabled={loading}
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="form-label" style={{ color: '#a3a3a3' }}>Especialización *</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                        placeholder="Ej: Degradados modernos, Barbería tradicional..."
                                        value={formData.specialization}
                                        onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                                        disabled={loading}
                                        required
                                    />
                                </div>
                                <div className="flex items-center gap-sm mt-md">
                                    <input
                                        type="checkbox"
                                        checked={formData.isIndependent}
                                        onChange={(e) => setFormData({ ...formData, isIndependent: e.target.checked })}
                                        disabled={loading}
                                        className="w-5 h-5 accent-amber-500 rounded text-amber-500 focus:ring-amber-500 bg-gray-900 border-gray-700"
                                        id="isIndependent"
                                    />
                                    <label htmlFor="isIndependent" className="form-label mb-0 cursor-pointer" style={{ color: '#e5e5e5' }}>
                                        Barbero Independiente (no staff)
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Social Media */}
                        <div className="mb-xl pb-xl" style={{ borderBottom: '1px solid #2a2a2a' }}>
                            <h2 className="font-bold mb-md" style={{ fontSize: '1.25rem', color: '#f59e0b' }}>
                                🌐 Redes Sociales
                            </h2>

                            <div className="space-y-md">
                                <div>
                                    <label className="form-label" style={{ color: '#a3a3a3' }}>📸 Instagram URL</label>
                                    <input
                                        type="url"
                                        className="form-input"
                                        style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                        placeholder="https://instagram.com/usuario"
                                        value={formData.instagramUrl}
                                        onChange={(e) => setFormData({ ...formData, instagramUrl: e.target.value })}
                                        disabled={loading}
                                    />
                                </div>
                                <div>
                                    <label className="form-label" style={{ color: '#a3a3a3' }}>📘 Facebook URL</label>
                                    <input
                                        type="url"
                                        className="form-input"
                                        style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                        placeholder="https://facebook.com/usuario"
                                        value={formData.facebookUrl}
                                        onChange={(e) => setFormData({ ...formData, facebookUrl: e.target.value })}
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Services */}
                        <div className="mb-xl">
                            <h2 className="font-bold mb-md" style={{ fontSize: '1.25rem', color: '#f59e0b' }}>
                                💈 Servicios que Brinda *
                            </h2>
                            <p className="text-sm mb-md" style={{ color: '#737373' }}>
                                Selecciona los servicios del catálogo. Puedes personalizar el precio de cada uno.
                            </p>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem' }}>
                                {availableServices.map(service => {
                                    const isSelected = selectedServiceIds.includes(service.id);
                                    return (
                                        <div
                                            key={service.id}
                                            style={{
                                                padding: '1rem',
                                                border: isSelected ? '2px solid #f59e0b' : '2px solid #2a2a2a',
                                                borderRadius: '0.75rem',
                                                backgroundColor: isSelected ? '#f59e0b10' : '#1a1a1a',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                boxShadow: isSelected ? '0 0 20px rgba(245, 158, 11, 0.15)' : 'none'
                                            }}
                                            onClick={() => !loading && toggleService(service.id)}
                                        >
                                            <div className="flex items-start gap-md">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => { }}
                                                    disabled={loading}
                                                    style={{ marginTop: '0.25rem', accentColor: '#f59e0b' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <div className="flex-1">
                                                    <p className="font-bold mb-xs" style={{ color: '#e5e5e5' }}>{service.name}</p>
                                                    <p className="text-sm mb-xs" style={{ color: '#a3a3a3' }}>{service.description}</p>
                                                    <p className="text-xs" style={{ color: '#737373' }}>
                                                        Precio sugerido: <strong style={{ color: '#f59e0b' }}>${service.suggestedPrice}</strong>
                                                    </p>

                                                    {isSelected && (
                                                        <div className="mt-md" onClick={(e) => e.stopPropagation()}>
                                                            <label className="form-label text-xs" style={{ color: '#a3a3a3' }}>Precio Personalizado (opcional)</label>
                                                            <input
                                                                type="number"
                                                                className="form-input"
                                                                style={{ background: '#111', border: '1px solid #333', color: '#e5e5e5', maxWidth: '200px' }}
                                                                placeholder={service.suggestedPrice.toString()}
                                                                value={customPrices[service.id] || ''}
                                                                onChange={(e) => handleCustomPriceChange(service.id, e.target.value)}
                                                                disabled={loading}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem',
                            paddingTop: '2rem',
                            borderTop: '1px solid #2a2a2a'
                        }} className="pt-lg">
                            <button
                                type="button"
                                className="btn"
                                style={{
                                    width: '100%',
                                    background: 'transparent',
                                    border: '1px solid #404040',
                                    color: '#a3a3a3',
                                    borderRadius: '0.75rem',
                                    padding: '0.875rem',
                                    fontWeight: '600',
                                    transition: 'all 0.2s'
                                }}
                                onClick={() => navigate('/admin/barbers')}
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn"
                                style={{
                                    width: '100%',
                                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                    border: 'none',
                                    color: '#000',
                                    borderRadius: '0.75rem',
                                    padding: '0.875rem',
                                    fontWeight: '700',
                                    fontSize: '1rem',
                                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
                                    transition: 'all 0.2s'
                                }}
                                disabled={loading}
                            >
                                {loading ? '💾 Creando...' : '💾 Crear Barbero'}
                            </button>
                        </div>
                    </div>
                </form>


            </div>
        </>
    );
};

export default CreateBarberPage;
